import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDebounceFn } from '@vueuse/core';
import { useAccountStore } from '~/stores/accountStore';
import { useFulltextSearchStore } from '~/stores/fulltextSearchStore';
import { fulltextSearch } from '~/utils/dataProviders/FulltextSearchProvider';
import { useBodyScrollLock } from './useBodyScrollLock';
import { useScreenSize } from './useScreenSize';
import { MEILISEARCH_SEARCH_INDEX_NAME } from '~/utils/constants';

const DEBOUNCE_DELAY = 200;
const MIN_SEARCH_LENGTH = 2;
const FULLTEXT_SEARCH_LIMIT = 10;

export const useFulltextSearch = () => {
    const fulltextSearchStore = useFulltextSearchStore();
    const { searchQuery, showSearchResults, searchResults, isLoading, isOverlayVisible } = storeToRefs(fulltextSearchStore);
    const { clearSearch, hideOverlay, hideResults, toggleOverlay } = fulltextSearchStore;
    const { state } = storeToRefs(useAccountStore());

    const viewAllUrl = computed(() => `/search?q=${searchQuery.value}`);

    const performSearch = async (query?: string) => {
        const searchTerm = query || searchQuery.value;
        isLoading.value = true;
        const { hits } = await fulltextSearch(searchTerm, MEILISEARCH_SEARCH_INDEX_NAME, {
            limit: FULLTEXT_SEARCH_LIMIT,
            filter: [`accountId = ${state.value.id}`],
            attributesToCrop: ['content'],
            cropLength: 7, // words
            attributesToHighlight: ['title', 'content'],
            highlightPreTag: '<strong>',
            highlightPostTag: '</strong>',
        });

        searchResults.value = hits;
        showSearchResults.value = searchQuery.value.length >=  MIN_SEARCH_LENGTH;
        isLoading.value = false;
    };

    const debouncedSearch = useDebounceFn(() => {
        performSearch();
    }, DEBOUNCE_DELAY);

    const onSearchInput = () => {
        if (searchQuery.value.length >= MIN_SEARCH_LENGTH) {
            debouncedSearch();
        } else {
            showSearchResults.value = false;
            searchResults.value = [];
        }
    };

    const onResultSelected = () => {
        clearSearch();
        hideOverlay();
        hideResults();
    };

    const lockScroll = useBodyScrollLock();
    watch(isOverlayVisible, (newVal) => lockScroll.value = newVal);

    const { isDesktop } = useScreenSize();
    watch(isDesktop, (newVal) => {
        if(!newVal) return;
        lockScroll.value = isOverlayVisible.value =  false;
    });

    return {
        isOverlayVisible,
        searchQuery,
        showSearchResults,
        searchResults,
        isLoading,
        viewAllUrl,
        hideOverlay,
        hideResults,
        toggleOverlay,
        performSearch,
        onSearchInput,
        clearSearch,
        onResultSelected,
    };
}; 
